import { Button } from '@/components/ui/button';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/hooks/use-toast';
import { updateSessionDetails, getAdminSessions } from '@/lib/api-calls';
import { useNavigate } from '@tanstack/react-router';
import { FormModal } from '@/components/form-modal';
import { z } from 'zod';

const formSchema = z.object({});

interface SessionDeleteButtonProps {
  session: AdminSession;
  from?: string;
  schoolId?: string;
  trigger?: React.ReactNode;
}

export const SessionDeleteButton = ({ session, from, schoolId, trigger }: SessionDeleteButtonProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync: updateSessionMutation } = useMutation({
    mutationFn: updateSessionDetails,
  });

  const handleArchive = async ({ closeModal }: { closeModal: () => void }) => {
    try {
      await updateSessionMutation({ id: session.id, data: { status: 'archived' } });
      toast({ title: 'Session deleted successfully' });
      closeModal();
      queryClient.invalidateQueries(getAdminSessions());

      let backLink = '/sessions';
      switch (from) {
        case 'past':
          backLink = '/sessions/past';
          break;
        case 'future':
          backLink = '/sessions/future';
          break;
        case 'sub':
          backLink = '/sessions/sub-sessions';
          break;
        case 'overlapping':
          backLink = '/sessions/overlapping';
          break;
        case 'school':
          backLink = schoolId ? `/orgs/schools/${schoolId}/sessions` : '/sessions';
          break;
        default:
          backLink = '/sessions';
          break;
      }

      navigate({
        to: backLink,
        search: {
          pageIndex: 0,
          pageSize: 50,
        },
      });
    } catch (error) {
      toast({
        title: 'Failed to delete session',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Delete Session"
      trigger={
        trigger || (
          <Button variant="outline" className="bg-slate-100 hover:bg-slate-200">
            Delete session
          </Button>
        )
      }
      onSubmit={handleArchive}
      formSchema={formSchema}
      submitButtonProps={{ variant: 'destructive', children: 'Delete Permanently' }}
      dialogContentProps={{ className: 'max-w-md' }}
    >
      {() => (
        <div className="my-6">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this session? This action will send a cancellation email to the teacher.
          </p>
        </div>
      )}
    </FormModal>
  );
};
